import * as React from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Upload, message, Skeleton } from 'antd';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import { withUserType } from '../withUserType';
import AIIcon from '../AIIcon';
import { owuploadCustomRequest } from '../../thunks/file_thunks';
import { getProtectedImageUriGeneric } from '../../utils/FileAccessUtils';

export function fileUploadValidation(file, fileMimeType) {
	const fileMimeTypes = [fileMimeType];
	const isFileType = fileMimeTypes.indexOf(file.type) !== -1;
	if (!isFileType) {
		message.error(`Uploaded file is not of the expected type: ${fileMimeType}`);
	}
	return isFileType;
}

const loadingCopies = [
	'Reading and structuring invoice data...',
	'Parsing invoice fields and values...',
	'Extracting vendor and billing details...',
	'Organizing charges and fees...',
	'Checking line items, taxes, and totals...',
	'Looking for discounts and adjustments...',
	'Validating amounts and calculations...',
	'Reviewing totals for accuracy...',
	'Preparing structured invoice data for review...',
	'Finalizing extracted data...',
];

export const InvoiceLoadingSkeleton: React.FC<{ currentLoadingCopyIndex?: number }> = ({
	currentLoadingCopyIndex = 0,
}) => {
	return (
		<div style={{ maxWidth: '800px', margin: '0 auto' }}>
			<div className="h3medium mb-6 text-left">{loadingCopies[currentLoadingCopyIndex]}</div>
			{/* Invoice Header - Company & Invoice Info */}
			<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '32px' }}>
				<Skeleton.Input active style={{ width: '180px' }} size="small" />
				<Skeleton.Input active style={{ width: '150px' }} size="small" />
			</div>

			{/* Bill To / Ship To Section */}
			<div style={{ display: 'flex', gap: '40px', marginBottom: '32px' }}>
				<div style={{ flex: 1 }}>
					<Skeleton active paragraph={{ rows: 3 }} title={false} />
				</div>
				<div style={{ flex: 1 }}>
					<Skeleton active paragraph={{ rows: 3 }} title={false} />
				</div>
			</div>

			{/* Line Items Table Header */}
			<div style={{ marginBottom: '16px' }}>
				<Skeleton.Input active style={{ width: '100%', height: '32px' }} />
			</div>

			{/* Line Items Rows */}
			<Skeleton active paragraph={{ rows: 4 }} title={false} style={{ marginBottom: '24px' }} />

			{/* Totals Section */}
			<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
				<div style={{ width: '300px' }}>
					<Skeleton active paragraph={{ rows: 3 }} title={false} />
				</div>
			</div>
		</div>
	);
};

export const InvoiceLoadingSkeletonAnimated: React.FC = () => {
	const [currentLoadingCopyIndex, setCurrentLoadingCopyIndex] = React.useState(0);

	React.useEffect(() => {
		const interval = setInterval(() => {
			setCurrentLoadingCopyIndex((prev) => (prev + 1) % loadingCopies.length);
		}, 2500);

		return () => clearInterval(interval);
	}, []);

	return <InvoiceLoadingSkeleton currentLoadingCopyIndex={currentLoadingCopyIndex} />;
};

class InvoiceUploadandParse extends React.Component<any, any> {
	loadingCopyInterval: any = null;

	constructor(props) {
		super(props);
		this.state = {
			uploadLoading: false,
			fileList: [],
			parsingLoading: false,
			fileName: '',
			localFileUrl: null,
			fileObject: null,
			currentLoadingCopyIndex: 0,
			s3FileUrl: null,
		};
	}

	handleUploadSuccess = (info) => {
		const { handleUploadSuccess } = this.props;
		const fileName = info.file && info.file.name ? info.file.name : '';

		let localFileUrl = this.state.localFileUrl;
		let fileObj = this.state.fileObject;
		const s3FileUrlFromState = this.state.s3FileUrl;

		if (!localFileUrl || !fileObj) {
			if (info.file) {
				if (info.file.originFileObj) {
					fileObj = info.file.originFileObj;
					// Create data URL if we don't have one
					if (!localFileUrl) {
						const reader = new FileReader();
						reader.onload = (e) => {
							const dataUrl = e.target.result;
							this.setState({ localFileUrl: dataUrl });
							// Send updated data to parent
							handleUploadSuccess({
								parsedData: nullSafeGet('data', info),
								fileUrl: s3FileUrlFromState || dataUrl,
								fileName: fileName,
								fileObject: fileObj,
							});
						};
						reader.readAsDataURL(fileObj);
						return;
					}
				} else if (info.file instanceof File || info.file instanceof Blob) {
					fileObj = info.file;
					// Create data URL if we don't have one
					if (!localFileUrl) {
						const reader = new FileReader();
						reader.onload = (e) => {
							const dataUrl = e.target.result;
							this.setState({ localFileUrl: dataUrl });
							// Send updated data to parent
							handleUploadSuccess({
								parsedData: nullSafeGet('data', info),
								fileUrl: s3FileUrlFromState || dataUrl,
								fileName: fileName,
								fileObject: fileObj,
							});
						};
						reader.readAsDataURL(fileObj);
						return;
					}
				}
			}
		}

		this.setState({
			uploadLoading: false,
			parsingLoading: false,
			fileName: fileName,
			localFileUrl: localFileUrl,
			fileObject: fileObj,
		});

		this.clearLoadingCopyInterval();

		if (this.props.onParsingComplete) {
			this.props.onParsingComplete();
		}

		const finalFileUrl = s3FileUrlFromState || localFileUrl;

		handleUploadSuccess({
			parsedData: nullSafeGet('data', info),
			fileUrl: finalFileUrl,
			fileName: fileName,
			fileObject: fileObj,
		});
	};

	onFileChange = ({ file, fileList }) => {
		const isParsingLoading = file.status === 'uploading' || file.status === 'done';

		// Store file details when file is selected
		if (file.status === 'uploading' && file.originFileObj) {
			// Create a data URL instead of blob URL for cross-tab access
			const reader = new FileReader();
			reader.onload = (e) => {
				const dataUrl = e.target.result;
				this.setState({
					uploadLoading: file.status === 'uploading',
					parsingLoading: isParsingLoading,
					localFileUrl: dataUrl,
					fileObject: file.originFileObj,
					fileName: file.name,
				});

				// Notify parent that file is selected and parsing has started
				if (this.props.onFileSelected) {
					this.props.onFileSelected({
						fileUrl: dataUrl,
						fileName: file.name,
						fileObject: file.originFileObj,
					});
				}
			};
			reader.readAsDataURL(file.originFileObj);
		} else {
			this.setState({
				uploadLoading: file.status === 'uploading',
				parsingLoading: isParsingLoading,
			});
		}

		if (isParsingLoading && !this.loadingCopyInterval) {
			this.startLoadingCopyInterval();
			// Notify parent that parsing has started
			if (this.props.onParsingStart) {
				this.props.onParsingStart();
			}
		} else if (!isParsingLoading && this.loadingCopyInterval) {
			this.clearLoadingCopyInterval();
		}
	};

	startLoadingCopyInterval = () => {
		this.setState({ currentLoadingCopyIndex: 0 });

		this.loadingCopyInterval = setInterval(() => {
			this.setState((prevState) => ({
				currentLoadingCopyIndex: (prevState.currentLoadingCopyIndex + 1) % loadingCopies.length,
			}));
		}, 2500);
	};

	clearLoadingCopyInterval = () => {
		if (this.loadingCopyInterval) {
			clearInterval(this.loadingCopyInterval);
			this.loadingCopyInterval = null;
		}
	};

	componentWillUnmount() {
		this.clearLoadingCopyInterval();

		// Cleanup object URL to prevent memory leaks (if using blob URLs)
		// Note: Data URLs don't need cleanup like blob URLs do
		if (this.state.localFileUrl && this.state.localFileUrl.startsWith('blob:')) {
			URL.revokeObjectURL(this.state.localFileUrl);
		}
	}

	render() {
		const { userType } = this.props;
		const { fileList, parsingLoading, fileName, currentLoadingCopyIndex } = this.state;

		function beforePDFUpload(file) {
			const pdfMimeTypes = ['application/pdf'];
			const isPDF = pdfMimeTypes.indexOf(file.type) !== -1;
			if (!isPDF) {
				message.error('You can only upload PDF files!');
			}
			const isLt2M = file.size / 1024 / 1024 < 4;
			if (!isLt2M) {
				message.error('PDF file size must smaller than 4MB!');
			}
			return isPDF && isLt2M;
		}

		const BACKEND_URI = getBackendUri();
		const token = retrieveCachedUserDetails(['token']).token;
		const action = `${BACKEND_URI}/api/v1/${userType}/invoice/file/invoice_pdf/extract_data`;

		const uploadProps = {
			action,
			onSuccess: this.handleUploadSuccess,
			onError: (error) => {
				console.log('Upload error:', error);
				this.setState({
					uploadLoading: false,
					parsingLoading: false,
				});
				this.clearLoadingCopyInterval();
				// Notify parent that parsing failed
				if (this.props.onParsingComplete) {
					this.props.onParsingComplete();
				}
			},
			fileList: fileList,
			multiple: false,
			beforeUpload: beforePDFUpload,
			showUploadList: { showPreviewIcon: true, showRemoveIcon: true },
			onChange: this.onFileChange,
			headers: { 'X-Auth-Token': token },
			accept: '.pdf',
		};

		const customUploadProps = {
			...uploadProps,
			customRequest: (options) => {
				const { file, onSuccess, onError, onProgress } = options;
				const formData = new FormData();
				formData.append('file', file);

				owuploadCustomRequest({
					userType,
					file,
					onSuccess: ({ data }) => {
						const s3Url = getProtectedImageUriGeneric(data.fileId, data.fileName);
						if (s3Url) {
							this.setState({ s3FileUrl: s3Url });
						}
					},
					onError: (error) => {
						console.error('S3 upload failed for invoice PDF:', error);
					},
				});

				const xhr = new XMLHttpRequest();
				xhr.open('POST', action, true);
				xhr.setRequestHeader('X-Auth-Token', token);

				xhr.upload.onprogress = (e) => {
					if (e.lengthComputable && onProgress) {
						onProgress({ percent: (e.loaded / e.total) * 100 });
					}
				};

				xhr.onload = () => {
					if (xhr.status === 200) {
						try {
							const response = JSON.parse(xhr.responseText);
							if (onSuccess) {
								onSuccess(response, xhr);
							}
						} catch (e) {
							if (onError) {
								onError(e);
							}
						}
					} else {
						if (onError) {
							onError(new Error(`Upload failed with status ${xhr.status}`));
						}
					}
				};

				xhr.onerror = () => {
					if (onError) {
						onError(new Error('Upload failed'));
					}
				};

				xhr.send(formData);
			},
		};

		return (
			<div>
				<Upload.Dragger {...customUploadProps} disabled={parsingLoading}>
					{parsingLoading ? (
						<InvoiceLoadingSkeleton currentLoadingCopyIndex={currentLoadingCopyIndex} />
					) : (
						<div className="flex flex-col items-center justify-center gap-6">
							<div className="rounded border-white  px-4 py-4 text-center ">
								<div className="h4medium mb-2 text-character-primary">
									<AIIcon /> AI Invoice Upload & Classification
								</div>
								<div className="bodyregular text-character-secondary">
									Upload invoices and OpenWrench automatically extracts and classifies each line
									item into labor, materials, parts, tax, and other categories - no manual entry
									required.
								</div>
								<Button className="mt-4" icon={<UploadOutlined />}>
									Click to Upload
								</Button>
							</div>
						</div>
					)}
				</Upload.Dragger>
			</div>
		);
	}
}

export default withUserType(InvoiceUploadandParse);
